/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import React, { memo, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import { Flex } from '@atlaskit/primitives/compiled';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { MediaClientConfig } from '@atlaskit/media-core';
import { Browser } from '@atlaskit/media-picker';
import type { UploadRejectionData } from '@atlaskit/media-picker';
import type { UploadEndEventPayload } from '@atlaskit/media-picker/types';
import { token } from '@atlaskit/tokens';

import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance';
import { keys as localStorageKeys } from '@confluence/storage-manager/entry-points/LocalStorageKeys';
import { useSubscriptionEvent } from '@confluence/graphql/entry-points/useSubscriptionEvent';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder';
import {
	COVER_PICTURE_MEDIA_UPLOAD_EXPERIENCE,
	COVER_PICTURE_UNSPLASH_UPLOAD_EXPERIENCE,
	COVER_PICTURE_REMOVE_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';
import { getAttachmentSizeLimitErrorFlag } from '@confluence/fabric-media-support/entry-points/attachmentSizeLimitErrorHelpers';
import { useMediaSSRModeAndAuthConfig } from '@confluence/fabric-media-support/entry-points/ssrMediaHelpers';
import {
	createEditorMediaProvider,
	createRendererMediaProvider,
} from '@confluence/fabric-media-support/entry-points/MediaProvider';
import { ExternalShareContext } from '@confluence/external-share-context/entry-points/ExternalShareContext';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags';
import { withFlags } from '@confluence/flags/entry-points/withFlags';
import { usePubSubEvent } from '@confluence/pubsub-client/entry-points/usePubSubEvent';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { getDraftShareIdFromQueryParam } from '@confluence/route-manager-utils/entry-points/getDraftShareIdFromQueryParam';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import type { COVERPICTUREWIDTH } from '@confluence/content-topper/entry-points/contentTopperConstants';
import { isExperimentEnabled } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations';
import { useUpdateContentProperty } from '@confluence/update-content-property-utils/entry-points/useUpdateContentProperty';

import { getRendererCoverPictureInfo, propertyToId } from '../coverPictureHelperFunctions';
import type {
	CoverPictureId,
	EditorPageCoverPictureComponentProps,
} from '../pageCoverPictureTypes';
import { CoverPictureIdPropertyKey } from '../pageCoverPictureTypes';
import {
	DEFAULT_POSITION_VALUE,
	CALCULATE_SCALED_COVER_IMAGE_HEIGHT_FOR_EDIT_PAGE,
} from '../pageCoverPictureConstants';
// eslint-disable-next-line graphql-relay-compat/no-experimental-directives -- Read http://go/cfe-no-experimental-directives
import { UpdateCoverPictureIdMutation } from '../UpdateCoverPictureIdMutation.experimentalgraphql.js';
import type { UpdateCoverPictureIdMutation as UpdateCoverPictureIdMutationType } from '../__types__/UpdateCoverPictureIdMutation';
import { CoverPictureQuery } from '../CoverPictureQuery.graphql';
import type {
	CoverPictureQuery as CoverPictureQueryType,
	CoverPictureQueryVariables,
} from '../__types__/CoverPictureQuery';

import type {
	GraphQLCoverPictureWidth,
	EditorPageCoverPictureUpdateCoverPictureWidthMutation as EditorPageCoverPictureUpdateCoverPictureWidthMutation$data,
	EditorPageCoverPictureUpdateCoverPictureWidthMutationVariables as EditorPageCoverPictureUpdateCoverPictureWidthMutation$variables,
} from './__types__/EditorPageCoverPictureUpdateCoverPictureWidthMutation';
// eslint-disable-next-line import/no-cycle
import { MediaCoverPicture } from './MediaCoverPicture';
import { ImagePicker } from './ImagePicker';
import { UnsplashCoverPicture } from './UnsplashCoverPicture';

const i18n = defineMessages({
	errorFlagTitle: {
		id: 'page-cover-picture.error.flag.title',
		description: 'Title within the error flag that appears if some upload image error occurred.',
		defaultMessage: 'We can’t upload your header image',
	},
	errorFlagDescription: {
		id: 'page-cover-picture.error.flag.description',
		description:
			'Description within the error flag that appears if some upload image error occurred.',
		defaultMessage: 'Refresh and try again.',
	},
	suggestHeaderNextTime: {
		id: `page-cover-picture.ai-suggested-image-header.flag.suggest-next-time`,
		description: 'Ask user if they would like us to suggest a header image next time',
		defaultMessage: 'Try a suggested header next time?',
	},
	suggestHeaderKeepSuggestions: {
		id: `page-cover-picture.ai-suggested-image-header.flag.suggest-next-time.accept`,
		// eslint-disable-next-line @atlassian/i18n/require-description
		description: 'Yes, keep suggestions',
		defaultMessage: 'Yes, keep suggestions',
	},
	suggestHeaderTurnOffSuggestions: {
		id: `page-cover-picture.ai-suggested-image-header.flag.suggest-next-time.decline`,
		description: 'No, turn off suggestions',
		defaultMessage: 'No, turn off',
	},
});

type CoverPictureProps = {
	hasCoverPicture: boolean;
	isUploadingPicture: boolean;
	userOffline: boolean;
	isTitleHovered?: boolean;
	width?: number;
	dynamicCoverImageHeight?: number;
	isReadOnly?: boolean;
	isFixedWidthImageOption?: boolean;
};

const COVER_IMAGE_FIXED_HEIGHT = 147;

// if page has cover picture or is uploading picture, make space for the photo
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CoverPictureWrapper = styled.div<CoverPictureProps>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: ({
		hasCoverPicture,
		isUploadingPicture,
		isFixedWidthImageOption,
		dynamicCoverImageHeight,
	}) =>
		(hasCoverPicture || isUploadingPicture) &&
		isFixedWidthImageOption &&
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		fg('confluence_frontend_cover_image_ssr')
			? `${COVER_IMAGE_FIXED_HEIGHT}px`
			: (hasCoverPicture || isUploadingPicture) &&
				  !isFixedWidthImageOption &&
				  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
				  fg('confluence_frontend_cover_image_ssr')
				? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
					CALCULATE_SCALED_COVER_IMAGE_HEIGHT_FOR_EDIT_PAGE
				: hasCoverPicture || isUploadingPicture
					? `${dynamicCoverImageHeight}px`
					: '0px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: ({ width, isFixedWidthImageOption, hasCoverPicture, isUploadingPicture }) =>
		(hasCoverPicture || isUploadingPicture) &&
		isFixedWidthImageOption &&
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		fg('confluence_frontend_cover_image_ssr')
			? `800px`
			: (hasCoverPicture || isUploadingPicture) &&
				  !isFixedWidthImageOption &&
				  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
				  fg('confluence_frontend_cover_image_ssr')
				? '100%'
				: (hasCoverPicture || isUploadingPicture) && width
					? `${width}px`
					: `100%`,
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	cursor: ({ isReadOnly }) => (!isReadOnly ? 'pointer' : 'auto'),
	marginBottom: token('space.100'),

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginTop: ({ hasCoverPicture, isUploadingPicture }) =>
		hasCoverPicture || isUploadingPicture ? token('space.075') : token('space.0'),
	borderRadius: 'unset',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ImageWrapper = styled.img<{
	coverPicturePosition?: number | null;
	dynamicCoverImageHeight?: number;
	isFixedWidthImageOption?: boolean;
}>({
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: ({ isFixedWidthImageOption, dynamicCoverImageHeight }) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		isFixedWidthImageOption && fg('confluence_frontend_cover_image_ssr')
			? `${COVER_IMAGE_FIXED_HEIGHT}px`
			: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
				!isFixedWidthImageOption && fg('confluence_frontend_cover_image_ssr')
				? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
					CALCULATE_SCALED_COVER_IMAGE_HEIGHT_FOR_EDIT_PAGE
				: `${dynamicCoverImageHeight}px`,
	objectFit: 'cover',
	position: 'absolute',
	'&:hover': {
		opacity: 1,
	},
	borderRadius: 'unset',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	objectPosition: ({ coverPicturePosition }) => `center ${coverPicturePosition}%`,
	aspectRatio: '38 / 7',
});

const EditorPageCoverPictureComponent = memo(
	({
		contentId,
		isTitleHovered,
		isPublishingContent,
		contentType,
		userOffline,
		setIsModalOpen,
		isModalOpen,
		hasCoverPicture,
		coverPictureState,
		isUnpublishedDraft,
		flags,
		floatingToolbarButtonStyle,
		width,
		dynamicCoverImageHeight,
		isReadOnly,
		spaceKey,
		aiSuggestedUnsplashImages,
		isFixedWidthImageOption,
		aiHeaderImageStatus,
		aiHeaderImageFileId,
		generateAIHeaderImage,
		setAIHeaderImageStatus,
	}: EditorPageCoverPictureComponentProps & WithFlagsProps) => {
		const [isMediaBrowserOpen, setIsMediaBrowserOpen] = useState<boolean>(false);
		const { isExternalShareRequest: isExternalShare } = useContext(ExternalShareContext);
		const [isUploadingPicture, setIsUploadingPicture] = useState<boolean>(false);
		const [isRegeneratingImage, setIsRegeneratingImage] = useState<boolean>(false);
		const [regenerationCount, setRegenerationCount] = useState<number>(0);
		// mediaConfig does not use onUploadRejection when in read only (RendererMediaProvider), but does need it otherwise (EditorMediaProvider).
		const [mediaConfig, setMediaConfig] = useState<{
			collection: string | undefined;
			authProvider: MediaClientConfig['authProvider'];
			onUploadRejection?: (data: UploadRejectionData) => boolean;
			useSha256ForUploads?: boolean;
		} | null>(null);
		// Using ref since onHandleUploadEnd event uses previous cover picture information and upload will fail
		// since the information is from the past render
		const coverPicture = useRef<{
			draft: CoverPictureId | null;
		}>({ draft: null });
		// need this force update since we are using useRef for the cover picture and therefore won't update the image automatically on change
		const [, forceUpdate] = useState<any>();
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const isLivePage = useIsCurrentPageLive();
		// need this state since we want to show reposition option right after we upload a photo
		const [shouldShowReposition, setShouldShowReposition] = useState<boolean>(false);
		const [shouldShowAIUnsplashHeaderToolbar, setShouldShowAIUnsplashHeaderToolbar] =
			useState<boolean>(false);

		const ssrConfig = useMediaSSRModeAndAuthConfig({ contentId, isExternalShare });
		const isServerPlaceholder = useRenderServerPlaceholder(true);
		const hasSSRConfigInSSRModeOrHydration =
			(isServerPlaceholder || window.__SSR_RENDERED__) &&
			ssrConfig &&
			fg('confluence_frontend_cover_image_ssr');

		const experienceTracker = useContext(ExperienceTrackerContext);
		const { formatMessage } = useIntl();
		const intl = useIntl();
		const [currentAIUnsplashImageIndex, setCurrentAIUnsplashImageIndex] = useState<number>(0);

		const { updateContentProperty } = useUpdateContentProperty({
			contentId,
			contentType: contentType || 'page',
			options: {
				refetchQueries: () => [
					{
						query: CoverPictureQuery,
						variables: {
							contentId,
							status: ['draft'],
							draftShareId: getDraftShareIdFromQueryParam(),
						},
					},
				],
			},
		});

		const { data, refetch } = useQuery<CoverPictureQueryType, CoverPictureQueryVariables>(
			// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Preloaded GraphQL Operation
			CoverPictureQuery,
			{
				context: { single: true },
				variables: {
					contentId,
					status: ['current', 'draft'],
					draftShareId: getDraftShareIdFromQueryParam(),
				},
			},
		);

		const coverPictureData = getRendererCoverPictureInfo(data, isLivePage);
		const coverPictureDataPosition = coverPictureData?.coverPicture?.published?.value?.position;
		const coverPictureDataId = coverPictureData?.coverPicture?.published?.value?.id;

		// Need this since the isUnsplash prop isn't getting the up to date values since the
		// parent component isn't subscribing to the pubsub event updates.
		const isUnsplashLocalCheck =
			coverPicture.current?.draft?.value?.id?.includes('images.unsplash.com');

		const isGeneratingHeaderImage =
			aiHeaderImageStatus === 'LOADING' || aiHeaderImageStatus === 'LOADING_WITH_ACCEPTANCE_FLOW';

		const [mutateCoverPictureIdExperimental] = useMutation<UpdateCoverPictureIdMutationType>(
			// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
			UpdateCoverPictureIdMutation,
			{
				refetchQueries: () => [
					{
						query: CoverPictureQuery,
						variables: {
							contentId,
							status: ['draft'],
							draftShareId: getDraftShareIdFromQueryParam(),
						},
					},
				],
			},
		);

		const [mutateCoverPictureWidth] = useMutation<
			EditorPageCoverPictureUpdateCoverPictureWidthMutation$data,
			EditorPageCoverPictureUpdateCoverPictureWidthMutation$variables
		>(
			gql`
				mutation EditorPageCoverPictureUpdateCoverPictureWidthMutation(
					$contentId: ID!
					$coverPictureWidth: GraphQLCoverPictureWidth!
				) {
					updateCoverPictureWidth(
						input: { contentId: $contentId, coverPictureWidth: $coverPictureWidth }
					) {
						content {
							id
						}
					}
				}
			`,
			{
				onError: (error) => {
					if (isUnauthorizedError(error)) {
						markErrorAsHandled(error);
					}
					getMonitoringClient().submitError(error, {
						attribution: Attribution.COMPANY_HUB,
					});
				},
				ignoreResults: true, // don't want to update cache with content info
			},
		);

		// update draft's cover id once we get the new info back from pubsub
		const onCoverPictureUpdated = useCallback(
			(_eventName: any, pubsubData: any) => {
				if (
					!['DUAL', 'GRAPHQL'].includes(
						StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', ''),
					) ||
					!fg('cc_fps_migration_cover-picture-updated')
				) {
					const value = JSON.parse(pubsubData.contentPropertyUpdateRequestBody.value);
					const version = pubsubData.contentPropertyUpdateRequestBody.version;
					void coverPictureState.setHasCoverPicture(true);
					coverPicture.current['draft'] = { value, version };
					setIsUploadingPicture(false);
					void coverPictureState.setIsUploadingPicture(false);
					setIsRegeneratingImage(false);
					forceUpdate(Math.random());
				}
			},
			[coverPictureState],
		);

		const onCoverPictureUpdatedSubscriptionData = useCallback(
			async (_: string, response: any) => {
				try {
					await refetch();
					const { coverPictureUpdated } = response || {};
					const value = JSON.parse(coverPictureUpdated?.value);
					const version = coverPictureUpdated?.version;
					coverPicture.current['draft'] = { value, version };
					void coverPictureState.setHasCoverPicture(true);
					setIsUploadingPicture(false);
					void coverPictureState.setIsUploadingPicture(false);
					void coverPictureState.setCoverPictureId(value.id);
					setIsRegeneratingImage(false);
					forceUpdate(Math.random());
				} catch (error) {
					getMonitoringClient().submitError(error, {
						attribution: Attribution.PAGE_EXPERIENCES,
					});
					markErrorAsHandled(error);
				}
			},
			[refetch, coverPictureState],
		);

		const onCoverPictureWidthUpdated = useCallback(
			(_eventName: any, pubsubData: any) => {
				if (
					!['DUAL', 'GRAPHQL'].includes(
						StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', ''),
					) ||
					!fg('cc_fps_migration_cover-picture-width-updated')
				) {
					const value = pubsubData.contentCoverPictureWidthRequestBody.coverPictureWidth;
					void coverPictureState.toggleCoverPictureWidthButton(value);
					forceUpdate(Math.random());
				}
			},
			[coverPictureState],
		);

		const onCoverPictureWidthUpdatedSubscription = useCallback(
			(_: string, response: any) => {
				const { coverPictureWidthUpdated } = response || {};

				const value = coverPictureWidthUpdated?.coverPictureWidth;
				void coverPictureState.toggleCoverPictureWidthButton(value);
				forceUpdate(Math.random());
			},
			[coverPictureState],
		);

		const onCoverPictureDeleted = useCallback(
			(_eventName: any, _data: any) => {
				if (
					!['DUAL', 'GRAPHQL'].includes(
						StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', ''),
					) ||
					!fg('cc_fps_migration_cover-picture-deleted')
				) {
					coverPicture.current['draft'] = null;
					void coverPictureState.setHasCoverPicture(false);
					forceUpdate(Math.random());
				}
			},
			[coverPictureState],
		);

		const onCoverPictureDeletedSubscription = useCallback(async () => {
			try {
				await refetch();
				coverPicture.current['draft'] = null;
				void coverPictureState.setHasCoverPicture(false);
				void coverPictureState.setCoverPictureId(null);
				forceUpdate(Math.random());
			} catch (error) {
				getMonitoringClient().submitError(error, {
					attribution: Attribution.PAGE_EXPERIENCES,
				});
				markErrorAsHandled(error);
			}
		}, [refetch, coverPictureState]);

		const pubSubResourceType = contentType || 'page';
		// pub sub setup and cleanup
		usePubSubEvent({
			contentId,
			contentType: pubSubResourceType,
			eventName: 'updated:coverPicture',
			onEvent: onCoverPictureUpdated,
			skip:
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				fg('cc_fps_migration_cover-picture-updated') &&
				fg('confluence_enable_subscriptions-context-provider') &&
				StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', '') ===
					'GRAPHQL',
		});

		useSubscriptionEvent({
			subscriberId: 'EditingCoverPictureSubscription',
			contentId,
			delta: 'coverPictureUpdated',
			contentType: pubSubResourceType,
			skip:
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				!fg('cc_fps_migration_cover-picture-updated') ||
				!fg('confluence_enable_subscriptions-context-provider') ||
				!['DUAL', 'GRAPHQL'].includes(
					StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', ''),
				),
			onDataReceived: onCoverPictureUpdatedSubscriptionData,
		});

		usePubSubEvent({
			contentId,
			contentType: pubSubResourceType,
			eventName: 'deleted:coverPicture',
			onEvent: onCoverPictureDeleted,
			skip:
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				fg('cc_fps_migration_cover-picture-deleted') &&
				fg('confluence_enable_subscriptions-context-provider') &&
				StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', '') ===
					'GRAPHQL',
		});

		useSubscriptionEvent({
			subscriberId: 'EditorCoverPictureDeletedSubscription',
			contentId,
			delta: 'coverPictureDeleted',
			contentType: pubSubResourceType,
			skip:
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				!fg('cc_fps_migration_cover-picture-deleted') ||
				!fg('confluence_enable_subscriptions-context-provider') ||
				!['DUAL', 'GRAPHQL'].includes(
					StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', ''),
				),
			onDataReceived: onCoverPictureDeletedSubscription,
		});

		usePubSubEvent({
			contentId,
			contentType: pubSubResourceType,
			eventName: 'updated:coverPictureWidth',
			onEvent: onCoverPictureWidthUpdated,

			skip:
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				fg('cc_fps_migration_cover-picture-width-updated') &&
				fg('confluence_enable_subscriptions-context-provider') &&
				StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', '') ===
					'GRAPHQL',
		});

		useSubscriptionEvent({
			subscriberId: 'EditorCoverPictureWidthUpdatedSubscription',
			contentId,
			delta: 'coverPictureWidthUpdated',
			contentType: pubSubResourceType,
			skip:
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				!fg('cc_fps_migration_cover-picture-width-updated') ||
				!fg('confluence_enable_subscriptions-context-provider') ||
				!['DUAL', 'GRAPHQL'].includes(
					StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', ''),
				),
			onDataReceived: onCoverPictureWidthUpdatedSubscription,
		});

		// Check for existing cover picture
		useEffect(() => {
			const properties = data?.content?.nodes?.[0]?.properties?.nodes || [];
			const draft = properties.find((p) => p?.key === CoverPictureIdPropertyKey.DRAFT);
			coverPicture.current = {
				draft: propertyToId(draft),
			};
			forceUpdate(Math.random());
		}, [data]);

		// Setup media config
		useEffect(() => {
			const showAttachmentLimitReachedFlag = (rejectionData: UploadRejectionData) => {
				if (flags && rejectionData) {
					void flags
						.showErrorFlag(getAttachmentSizeLimitErrorFlag(rejectionData, intl))
						.then(() => {
							setIsUploadingPicture(false);
							void coverPictureState.setIsUploadingPicture(false);

							experienceTracker.abort({
								name: COVER_PICTURE_MEDIA_UPLOAD_EXPERIENCE,
								reason: 'Cover picture media upload too large',
							});

							createAnalyticsEvent({
								type: 'sendScreenEvent',
								data: {
									name: 'pageCoverPictureAttachmentTooLargeFlag',
								},
							}).fire();
						});
				}
				return !!rejectionData;
			};

			// If read only, use renderer media provider, as we only need the auth provider from rendererMediaProvider, and user without edit perms (read only) doesn't have access to editorMediaProvider APIs
			// If not read only, use editor media provider
			const createMediaConfig = async () => {
				// Media provider is not supported in SSR, skip it for now.
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				if (process.env.REACT_SSR) {
					return;
				}
				if (isReadOnly) {
					const { viewMediaClientConfig } = await createRendererMediaProvider({
						contentId,
						isExternalShare: false,
					});
					if (!viewMediaClientConfig) {
						return null;
					}
					setMediaConfig({
						collection: `contentId-${contentId}`,
						authProvider: viewMediaClientConfig.authProvider,
					});
				} else {
					const { uploadParams, uploadMediaClientConfig } = await createEditorMediaProvider({
						contentId,
						contentType: 'page',
						isExternalShare,
						onUploadRejection: flags ? showAttachmentLimitReachedFlag : undefined,
					});

					if (uploadMediaClientConfig == undefined) {
						return null;
					}
					setMediaConfig({
						collection: uploadParams?.collection,
						authProvider: uploadMediaClientConfig.authProvider,
						onUploadRejection: uploadParams?.onUploadRejection,
						useSha256ForUploads: uploadMediaClientConfig?.useSha256ForUploads,
					});
				}
			};

			void createMediaConfig();
		}, [
			contentId,
			isExternalShare,
			flags,
			intl,
			coverPictureState,
			createAnalyticsEvent,
			experienceTracker,
			isReadOnly,
		]);

		// handling user publishes - we only need to worry about firing the analytic event as backend will take care of the
		// published cover picture content property mutation due to scheduled publishes
		useEffect(() => {
			// fire analytic if we're publishing a page with a cover image
			if (isPublishingContent && !!coverPicture.current?.draft?.value.id) {
				if (isUnpublishedDraft) {
					if (coverPictureState.state.isAIUnsplashImage) {
						createAnalyticsEvent({
							type: 'sendTrackEvent',
							data: {
								action: 'published',
								actionSubject: 'aiUnsplashImage',
								actionSubjectId: 'aiSuggestedUnsplashImage',
								source: 'editorPageCoverPicture',
								objectId: contentId,
							},
						}).fire();
					} else {
						createAnalyticsEvent({
							type: 'sendTrackEvent',
							data: {
								action: 'createdWithHeaderImage',
								actionSubject: 'page',
								objectId: contentId,
								source: 'editorPageCoverPicture',
							},
						}).fire();
					}
				} else {
					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							action: 'updatedWithHeaderImage',
							actionSubject: 'page',
							objectId: contentId,
							source: 'editorPageCoverPicture',
						},
					}).fire();
				}
			}
		}, [
			contentId,
			isPublishingContent,
			isUnpublishedDraft,
			createAnalyticsEvent,
			coverPictureState.state.isAIUnsplashImage,
		]);

		useEffect(() => {
			if (isPublishingContent && hasCoverPicture) {
				void coverPictureState.setShouldShowReposition(false);
			}
		}, [hasCoverPicture, isPublishingContent, coverPictureState]);

		const showErrorFlag = () => {
			void flags.showFlag({
				type: 'error',
				title: formatMessage(i18n.errorFlagTitle),
				description: formatMessage(i18n.errorFlagDescription),
				messageType: 'transactional',
				messageId: 'page-cover-picture-error-flag',
			});
			setIsUploadingPicture(false);
			void coverPictureState.setIsUploadingPicture(false);

			experienceTracker.stopOnError({
				name: COVER_PICTURE_MEDIA_UPLOAD_EXPERIENCE,
				error: new Error('Error with cover picture media upload'),
			});

			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'pageCoverPictureErrorFlag',
				},
			}).fire();
		};

		const handleUploadStart = () => {
			setIsUploadingPicture(true);
			void coverPictureState.setIsUploadingPicture(true);
			void coverPictureState.setIsChangingPicture(false);
			experienceTracker.start({
				name: COVER_PICTURE_MEDIA_UPLOAD_EXPERIENCE,
			});
		};

		const handleUploadEnd = (event: UploadEndEventPayload) => {
			const fileType = event.file.type;
			const id = event?.file?.id;
			if (!fileType.includes('image')) {
				showErrorFlag();
			} else if (id) {
				// must JSON stringify propertyValue object since mutation expects a string for this parameter
				const propertyValue = JSON.stringify({
					id,
					position: DEFAULT_POSITION_VALUE,
				});
				const version = String((coverPicture.current?.draft?.version || 0) + 1);

				if (isExperimentEnabled('migrate_off_experimentalupdatecontentproperty')) {
					void updateContentProperty({
						propertyKey: CoverPictureIdPropertyKey.DRAFT,
						propertyValue,
						isDelete: false,
						hasExistingProperty: hasCoverPicture,
					});
				} else {
					void mutateCoverPictureIdExperimental({
						variables: {
							contentId,
							propertyValue,
							propertyKey: CoverPictureIdPropertyKey.DRAFT,
							versionNumber: version,
						},
					});
				}

				setShouldShowReposition(true);
				void coverPictureState.setShouldShowReposition(true);
				experienceTracker.succeed({
					name: COVER_PICTURE_MEDIA_UPLOAD_EXPERIENCE,
				});
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'added',
						actionSubject: 'image',
						actionSubjectId: 'headerImageAdded',
						source: 'editorPageCoverPicture',
						attributes: {
							imageSource: 'uploaded',
							imageId: id,
							inputMethod: 'header',
							isLivePage,
						},
					},
				}).fire();
			} else {
				showErrorFlag();
			}
		};

		const handleUnsplashUpload = useCallback(
			(photoURL, triggeredFromRegenerate = false) => {
				if (!triggeredFromRegenerate) {
					setIsUploadingPicture(true);
					void coverPictureState.setIsUploadingPicture(true);
				}
				const propertyValue = JSON.stringify({
					id: photoURL,
					position: DEFAULT_POSITION_VALUE,
				});
				const version = String((coverPicture.current?.draft?.version || 0) + 1);
				if (isExperimentEnabled('migrate_off_experimentalupdatecontentproperty')) {
					void updateContentProperty({
						propertyKey: CoverPictureIdPropertyKey.DRAFT,
						propertyValue,
						isDelete: false,
						hasExistingProperty: hasCoverPicture,
					});
				} else {
					void mutateCoverPictureIdExperimental({
						variables: {
							contentId,
							propertyValue,
							propertyKey: CoverPictureIdPropertyKey.DRAFT,
							versionNumber: version,
						},
					});
				}
				if (coverPictureState.state.isAIUnsplashImage) {
					setShouldShowReposition(false);
					setShouldShowAIUnsplashHeaderToolbar(true);
					void coverPictureState.setShouldShowAIUnsplashHeaderToolbar(true);
				} else {
					setShouldShowReposition(true);
					void coverPictureState.setShouldShowReposition(true);
				}
				experienceTracker.succeed({
					name: COVER_PICTURE_UNSPLASH_UPLOAD_EXPERIENCE,
				});
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'added',
						actionSubject: 'image',
						actionSubjectId: 'headerImageAdded',
						source: 'editorPageCoverPicture',
						attributes: {
							imageSource: 'unsplash',
							imageId: photoURL,
							inputMethod: 'header',
							isLivePage,
						},
					},
				}).fire();
			},
			[
				contentId,
				coverPictureState,
				createAnalyticsEvent,
				experienceTracker,
				isLivePage,
				mutateCoverPictureIdExperimental,
				updateContentProperty,
				hasCoverPicture,
			],
		);

		const handleAIGeneratedUpload = useCallback(
			(mediaFileId: string) => {
				const propertyValue = JSON.stringify({
					id: mediaFileId,
					position: DEFAULT_POSITION_VALUE,
				});
				const version = String((coverPicture.current?.draft?.version || 0) + 1);
				if (isExperimentEnabled('migrate_off_experimentalupdatecontentproperty')) {
					void updateContentProperty({
						propertyKey: CoverPictureIdPropertyKey.DRAFT,
						propertyValue,
						isDelete: false,
						hasExistingProperty: hasCoverPicture,
					});
				} else {
					void mutateCoverPictureIdExperimental({
						variables: {
							contentId,
							propertyValue,
							propertyKey: CoverPictureIdPropertyKey.DRAFT,
							versionNumber: version,
						},
					});
				}
				setAIHeaderImageStatus?.('SUCCESSFULLY_SET');
			},
			[
				contentId,
				coverPicture,
				mutateCoverPictureIdExperimental,
				setAIHeaderImageStatus,
				updateContentProperty,
				hasCoverPicture,
			],
		);

		const updateCoverPictureState = useCallback(async () => {
			await coverPictureState.setHasCoverPicture(true);
		}, [coverPictureState]);

		useEffect(() => {
			if (
				aiSuggestedUnsplashImages?.[0] &&
				!isUploadingPicture &&
				!coverPictureState.state.hasCoverPicture
			) {
				try {
					handleUnsplashUpload(
						aiSuggestedUnsplashImages?.[currentAIUnsplashImageIndex]?.fullSizeImageUrl,
					);
				} finally {
					void updateCoverPictureState();
				}
			}
		}, [
			currentAIUnsplashImageIndex,
			aiSuggestedUnsplashImages,
			isUploadingPicture,
			handleUnsplashUpload,
			coverPictureState.state.hasCoverPicture,
			coverPictureState.setHasCoverPicture,
			updateCoverPictureState,
		]);

		useEffect(() => {
			if (!isExperimentEnabled('confluence_aifc_header_image_generation_experiment')) {
				return;
			}

			if (isGeneratingHeaderImage) {
				void coverPictureState.setIsUploadingPicture(true);
			} else {
				void coverPictureState.setIsUploadingPicture(false);
				if (aiHeaderImageFileId && aiHeaderImageStatus !== 'SUCCESSFULLY_SET') {
					try {
						handleAIGeneratedUpload(aiHeaderImageFileId);
					} finally {
						void updateCoverPictureState();
					}
				}
			}
		}, [
			isGeneratingHeaderImage,
			coverPictureState,
			aiHeaderImageFileId,
			handleAIGeneratedUpload,
			updateCoverPictureState,
			aiHeaderImageStatus,
		]);

		const onAcceptAIUnsplashImage = useCallback(async () => {
			setShouldShowReposition(true);
			setShouldShowAIUnsplashHeaderToolbar(false);
			void coverPictureState.setShouldShowAIUnsplashHeaderToolbar(false);
			void coverPictureState.setShouldShowReposition(true);
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'accepted',
					actionSubject: 'aiUnsplashImage',
					actionSubjectId: 'aiSuggestedUnsplashImage',
					source: 'editorPageCoverPicture',
					attributes: {
						regenerationCount,
					},
				},
			}).fire();
		}, [coverPictureState, regenerationCount, createAnalyticsEvent]);

		const onRegenerate = useCallback(async () => {
			if (aiSuggestedUnsplashImages && aiSuggestedUnsplashImages.length > 0) {
				// This is used for to tell the toolbar that the image is regenerating
				setIsRegeneratingImage(true);
				// This is used for analytics to track if the user has regenerated and if they ultimately accepted or declined
				setRegenerationCount(regenerationCount + 1);
				// This will loop through the images in the array and circle back to the first image if the last image is reached
				// The images get less and less relevant as the index increases so it's better to start from the beginning
				// Than it is to get the next 30 results from the API
				const nextIndex = (currentAIUnsplashImageIndex + 1) % aiSuggestedUnsplashImages.length;
				setCurrentAIUnsplashImageIndex(nextIndex);
				handleUnsplashUpload(aiSuggestedUnsplashImages?.[nextIndex]?.fullSizeImageUrl, true);
			}
		}, [
			currentAIUnsplashImageIndex,
			aiSuggestedUnsplashImages,
			handleUnsplashUpload,
			setIsRegeneratingImage,
			setRegenerationCount,
			regenerationCount,
		]);

		const sendAIUnsplashOptInAnalytics = useCallback(
			(optIn: boolean) => {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'optIn',
						actionSubject: 'aiUnsplashImage',
						actionSubjectId: 'aiSuggestedUnsplashImage',
						source: 'editorPageCoverPicture',
						attributes: {
							optIn,
						},
					},
				}).fire();
			},
			[createAnalyticsEvent],
		);

		const showAIUnsplashHeaderRemovedFlag = useCallback(() => {
			if (localStorage.getItem(localStorageKeys.AI_UNSPLASH_HEADER_ACCEPTED) !== null) {
				return;
			}

			void flags.showFlag({
				title: formatMessage(i18n.suggestHeaderNextTime),
				type: 'info',
				id: 'ai-suggested-image-header-removed',
				actions: [
					{
						content: formatMessage(i18n.suggestHeaderKeepSuggestions),
						onClick: () => {
							localStorage.setItem(localStorageKeys.AI_UNSPLASH_HEADER_ACCEPTED, 'true');
							sendAIUnsplashOptInAnalytics(true);
							void flags.hideFlag('ai-suggested-image-header-removed');
						},
					},
					{
						content: formatMessage(i18n.suggestHeaderTurnOffSuggestions),
						onClick: () => {
							localStorage.setItem(localStorageKeys.AI_UNSPLASH_HEADER_ACCEPTED, 'false');
							sendAIUnsplashOptInAnalytics(false);
							void flags.hideFlag('ai-suggested-image-header-removed');
						},
					},
				],
			});

			setTimeout(() => {
				void flags.hideFlag('ai-suggested-image-header-removed');
			}, 8000);
		}, [flags, formatMessage, sendAIUnsplashOptInAnalytics]);

		const onRemoveImage = useCallback(async () => {
			void coverPictureState.setIsRemovingPicture(true);
			if (isExperimentEnabled('migrate_off_experimentalupdatecontentproperty')) {
				void updateContentProperty({
					propertyKey: CoverPictureIdPropertyKey.DRAFT,
					isDelete: true,
					hasExistingProperty: hasCoverPicture,
				});
			} else {
				void mutateCoverPictureIdExperimental({
					variables: {
						contentId,
						propertyKey: CoverPictureIdPropertyKey.DRAFT,
						isDelete: true,
					},
				});
			}
			void coverPictureState.setIsChangingPicture(false);
			if (coverPictureState.state.isAIUnsplashImage) {
				void coverPictureState.setIsAIUnsplashImage(false);
				void coverPictureState.setAISuggestedUnsplashImages(undefined);
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'removed',
						actionSubject: 'aiUnsplashImage',
						actionSubjectId: 'aiSuggestedUnsplashImage',
						source: 'editorPageCoverPicture',
						attributes: {
							regenerationCount,
						},
					},
				}).fire();
				showAIUnsplashHeaderRemovedFlag();
			}
			void coverPictureState.setIsRemovingPicture(false);
			experienceTracker.succeed({
				name: COVER_PICTURE_REMOVE_EXPERIENCE,
			});
		}, [
			contentId,
			mutateCoverPictureIdExperimental,
			coverPictureState,
			experienceTracker,
			showAIUnsplashHeaderRemovedFlag,
			createAnalyticsEvent,
			regenerationCount,
			updateContentProperty,
			hasCoverPicture,
		]);

		const onImageWidthChange = useCallback(
			async (coverPictureWidth: COVERPICTUREWIDTH) => {
				if (coverPictureWidth !== coverPictureState.state.coverPictureWidth) {
					void coverPictureState.toggleCoverPictureWidthButton(coverPictureWidth);

					//hack the input and outout have different capitalizations TT_TT
					// the mutation returns lowercase, the mutation expects uppercase
					await mutateCoverPictureWidth({
						variables: {
							contentId,
							// Casting to GraphQLCoverPictureWidth as structurally equivalent enums are not equal in TS
							coverPictureWidth: coverPictureWidth.toUpperCase() as GraphQLCoverPictureWidth,
						},
					});
				}
			},
			[contentId, mutateCoverPictureWidth, coverPictureState],
		);

		const onSaveReposition = async (position) => {
			// check that the position isn't the same as the current one
			if (position != coverPicture.current?.draft?.value.position) {
				const version = String((coverPicture.current?.draft?.version || 0) + 1);
				const propertyValue = JSON.stringify({
					id: coverPicture.current?.draft?.value.id,
					position,
				});
				coverPicture.current.draft!.value.position = position;
				coverPicture.current.draft!.version = parseInt(version);
				if (isExperimentEnabled('migrate_off_experimentalupdatecontentproperty')) {
					void updateContentProperty({
						propertyKey: CoverPictureIdPropertyKey.DRAFT,
						propertyValue,
						isDelete: false,
						hasExistingProperty: hasCoverPicture,
					});
				} else {
					void mutateCoverPictureIdExperimental({
						variables: {
							contentId,
							propertyValue,
							propertyKey: CoverPictureIdPropertyKey.DRAFT,
							versionNumber: version,
						},
					});
				}
			}
		};

		const openMediaBrowser = () => {
			setIsMediaBrowserOpen(true);
		};

		const closeMediaBrowser = () => {
			setIsMediaBrowserOpen(false);
		};

		const createLiveDocHeaderImageAnalyticsEvent = useCallback(
			(isUnsplashImage: boolean) => {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'viewed',
						actionSubject: 'LiveDocHeaderImage',
						actionSubjectId: 'liveDocHeaderImage',
						source: 'liveDocHeaderImage',
						attributes: {
							headerImageType: isUnsplashImage ? 'unsplash' : 'user-uploaded',
						},
					},
				}).fire();
			},
			[createAnalyticsEvent],
		);

		useEffect(() => {
			if (hasCoverPicture && isLivePage && isUnsplashLocalCheck != null) {
				createLiveDocHeaderImageAnalyticsEvent(isUnsplashLocalCheck);
			}
		}, [isUnsplashLocalCheck, isLivePage, hasCoverPicture, createLiveDocHeaderImageAnalyticsEvent]);

		return (
			<Flex>
				{hasCoverPicture && fg('confluence_frontend_cover_image_ssr') && (
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles
					<style
						// eslint-disable-next-line react/no-danger
						dangerouslySetInnerHTML={{
							__html: `
									@container editor-area (width < 760px) {
										.cover-picture-wrapper-dangerously-set-inner-html {
											height: 147px
										}
									}
									@container editor-area (width >= 4071px) {
										.cover-picture-wrapper-dangerously-set-inner-html {
											height: 750px
										}
									}
									`,
						}}
					/>
				)}
				<CoverPictureWrapper
					hasCoverPicture={hasCoverPicture}
					isUploadingPicture={isUploadingPicture}
					data-testid="cover-picture-wrapper"
					userOffline={userOffline}
					width={width}
					dynamicCoverImageHeight={dynamicCoverImageHeight}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
					className="cover-picture-wrapper-dangerously-set-inner-html"
					isFixedWidthImageOption={isFixedWidthImageOption}
					isReadOnly={isReadOnly}
				>
					{hasCoverPicture &&
						(mediaConfig || hasSSRConfigInSSRModeOrHydration) &&
						(isUnsplashLocalCheck ? (
							<UnsplashCoverPicture
								coverPicture={coverPicture}
								dynamicCoverImageHeight={dynamicCoverImageHeight}
								isFixedWidthImageOption={isFixedWidthImageOption}
								coverPictureDataPosition={coverPictureDataPosition}
								coverPictureDataId={coverPictureDataId}
							/>
						) : (
							<MediaCoverPicture
								coverPicture={coverPicture}
								mediaConfig={mediaConfig}
								ssrConfig={ssrConfig}
								contentId={contentId}
								showErrorFlag={showErrorFlag}
								hasSSRConfigInSSRModeOrHydration={hasSSRConfigInSSRModeOrHydration}
								isFixedWidthImageOption={isFixedWidthImageOption}
								isAIGenerated={!!aiHeaderImageFileId}
							/>
						))}
					{!isReadOnly && (
						<ImagePicker
							hasCoverPicture={hasCoverPicture}
							isTitleHovered={isTitleHovered}
							openMediaBrowser={openMediaBrowser}
							onRemoveImage={onRemoveImage}
							userOffline={userOffline}
							isUploadingPicture={isUploadingPicture}
							handleUnsplashUpload={handleUnsplashUpload}
							setIsUploadingPicture={setIsUploadingPicture}
							setIsModalOpen={setIsModalOpen}
							isModalOpen={isModalOpen}
							initialPicturePosition={coverPicture.current.draft?.value.position}
							onSaveReposition={onSaveReposition}
							shouldShowReposition={shouldShowReposition}
							setShouldShowReposition={setShouldShowReposition}
							coverPictureState={coverPictureState}
							floatingToolbarButtonStyle={floatingToolbarButtonStyle}
							forceUpdate={forceUpdate}
							contentId={contentId}
							dynamicCoverImageHeight={dynamicCoverImageHeight}
							onImageWidthChange={onImageWidthChange}
							spaceKey={spaceKey}
							isRegeneratingImage={isRegeneratingImage}
							onRegenerate={onRegenerate}
							onAcceptAIUnsplashImage={onAcceptAIUnsplashImage}
							shouldShowAIUnsplashHeaderToolbar={shouldShowAIUnsplashHeaderToolbar}
							isFixedWidthImageOption={isFixedWidthImageOption}
							generateAIHeaderImage={generateAIHeaderImage}
						/>
					)}
				</CoverPictureWrapper>
				{!isReadOnly && mediaConfig && (
					<Browser
						mediaClientConfig={{
							authProvider: mediaConfig?.authProvider,
							useSha256ForUploads: mediaConfig?.useSha256ForUploads,
						}}
						config={{
							multiple: false,
							fileExtensions: ['.jpg', '.jpeg', '.png', '.gif'],
							uploadParams: {
								collection: mediaConfig.collection,
								onUploadRejection: mediaConfig?.onUploadRejection,
							},
						}}
						isOpen={isMediaBrowserOpen}
						onClose={closeMediaBrowser}
						onUploadsStart={handleUploadStart}
						onError={showErrorFlag}
						onEnd={handleUploadEnd}
					/>
				)}
			</Flex>
		);
	},
);

export const EditorPageCoverPicture = withFlags(
	withTransparentErrorBoundary({
		attribution: Attribution.PAGE_EXTENSIONS,
	})(EditorPageCoverPictureComponent),
);
